"use client";

import { useEffect, useRef } from "react";
import Image from "next/image";
import type { AnimationItem } from "lottie-web";
import { inApp } from "@utils/platformDetection.client";
import assetLoader from "@utils/internalImageLoder";
import styles from "./PlatformLoader.module.scss";

interface PlatformLoaderProps {
  width?: number;
  height?: number;
  className?: string;
  loop?: boolean;
  autoplay?: boolean;
}

const PlatformLoader: React.FC<PlatformLoaderProps> = ({
  width = 80,
  height = 80,
  className = "",
  loop = true,
  autoplay = true,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const animationRef = useRef<AnimationItem | null>(null);
  const isInApp = inApp();

  useEffect(() => {
    if (isInApp || !containerRef.current) {
      return;
    }

    let isMounted = true;

    const loadAnimation = async () => {
      const [{ default: lottie }, { default: animationData }] =
        await Promise.all([
          import("lottie-web"),
          import("@components/atoms/LottieLoader/car-loader-dark.json"),
        ]);

      if (!isMounted || !containerRef.current) {
        return;
      }

      animationRef.current = lottie.loadAnimation({
        container: containerRef.current,
        renderer: "svg",
        loop,
        autoplay,
        animationData,
      });
    };

    loadAnimation();

    return () => {
      isMounted = false;
      animationRef.current?.destroy();
      animationRef.current = null;
    };
  }, [isInApp, loop, autoplay]);

  if (isInApp) {
    return (
      <div
        className={`${styles.container} ${className}`}
        style={{ width: `${width}px`, height: `${height}px` }}
      >
        <Image
          src={`${assetLoader()}/Loader/AppLoader.svg`}
          alt="Loading..."
          className={styles.svgLoader}
          width={width}
          height={height}
          unoptimized
          loading="eager"
        />
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={`${styles.container} ${className}`}
      style={{ width: `${width}px`, height: `${height}px` }}
    />
  );
};

export default PlatformLoader;
